import { useRef } from "react";
import cn from "classnames";
import Checkbox from "@components/new/Checkbox";
import { Title3 } from "@components/new/Typo";
import { Tooltip, TooltipWithRef } from '@components/new/Tooltip';
import { TableColumn, TableRowType } from "./TableTypes.ts";
import styles from "./Table.module.scss";

export type TableRowProps<T> = {
  index: number;
  id: string;
  row: T;
  isSelected: boolean;
  isOpen?: boolean,
  isCollapsible?: boolean,
  isNested?: boolean,
  onToggleIsOpen?: (id: string) => void,
  onToggleSelect: (id: string) => void;
  columns: TableColumn<T>[];
  selectable?: boolean;
  isRowSelectable?: (data: T) => boolean;
  isCheckboxDisabled?: (data: T) => boolean;
  checkboxTooltip?: (data: T) => string;
  rowClassName?: string | ((row: TableRowType<T>) => string);
  rowOnClick?: (row: T) => void;
  rowTooltip?: (row: T) => string;
  verticalAlignment?: "top" | "center";
};

function TableRow<T>(props: TableRowProps<T>): JSX.Element {
  const trRef = useRef<HTMLTableRowElement>(null);

  const rowClassName: string = (typeof props.rowClassName === "function")
    ? props.rowClassName({
        index: props.index,
        data: props.row,
        isSelected: props.isSelected,
      })
    : props.rowClassName ?? "";

  const tooltipText = (props.rowTooltip && props.rowTooltip(props.row));

  return (
    <tr
      ref={trRef}
      className={cn(
        styles.row,
        props.rowOnClick && styles.clickable,
        props.isSelected && styles.selected,
        rowClassName,
      )}
      onClick={() => props.rowOnClick?.(props.row)}
    >
      {tooltipText ? (
        <TooltipWithRef
          triggerRef={trRef as React.RefObject<HTMLElement>}
          position="top"
          text={tooltipText}
          whiteSpace="normal"
          maxWidth={320}
        />
      ) : null}
      {props.selectable && (
        <td
          className={cn(
            styles.cell,
            styles["alignment-left"],
            styles[`vAlignment-${props.verticalAlignment ?? 'center'}`],
          )}
        >
          <div className={styles.content}>
            {(
              (typeof props.isRowSelectable !== "function")
              || props.isRowSelectable(props.row)
            ) ? (
              <Tooltip
                action={
                  <Checkbox
                    checked={props.isSelected}
                    onChange={() => props.onToggleSelect(props.id)}
                    disabled={(typeof props.isCheckboxDisabled === "function") &&
                      props.isCheckboxDisabled(props.row)
                    }
                  />
                }
                text={props.checkboxTooltip && props.checkboxTooltip(props.row)}
                position="top"
              />
            ) : null}
          </div>
        </td>
      )}

      {props.columns.map((column, index) => (
        <td
          className={cn(
            styles.cell,
            styles[`alignment-${column.alignment ?? 'left'}`],
            styles[`vAlignment-${props.verticalAlignment ?? 'center'}`],
          )}
          key={index}
          style={
            column.fixWidth === true
              ? { width: column.width }
              : { maxWidth: column.width }
          }
        >
          <div className={styles.content}>
            {column.valueGetter ? (
              <Title3>{column.valueGetter(props.row)}</Title3>
            ) : (
              column.renderCell ? (column.renderCell({
                index: props.index,
                data: props.row,
                isSelected: props.isSelected,
                isOpen: props.isOpen,
                isCollapsible: props.isCollapsible,
                isNested: props.isNested,
                toggleIsOpen: () => { props.onToggleIsOpen && props.onToggleIsOpen(props.id)},
              })) : null
            )}
          </div>
        </td>
      ))}
    </tr>
  );
}

export default TableRow;
